class IgStoneDetails extends HTMLElement {
  #variantMap = {};
  #productFallback = {};
  #variantCache = new Map();
  #abortController = new AbortController();
  #requestController;
  #selectionSequence = 0;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#variantMap = this.#readJson('[data-stone-variant-map]') || {};
    this.#productFallback = this.#readJson('[data-stone-product-fallback]') || {};

    document.addEventListener('shopify:product:select', this.#handleVariantChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#requestController?.abort();
    this.#abortController.abort();
  }

  #readJson = (selector, root = this) => {
    const data = root.querySelector(selector);
    if (!data?.textContent) return null;

    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      console.error('Stone detail data could not be read.', error);
      return null;
    }
  };

  #handleVariantChange = async (event) => {
    if (String(event?.product?.id || '').split('/').pop() !== this.dataset.productId) return;

    const sequence = ++this.#selectionSequence;
    const eventVariantId = String(event?.detail?.variantId || '').split('/').pop();
    const eventDetails = eventVariantId ? this.#variantMap[eventVariantId] : null;
    this.#renderVariant(eventDetails || this.#productFallback);

    if (!event?.promise) return;

    try {
      const { variant } = await event.promise;
      if (sequence !== this.#selectionSequence || !this.isConnected) return;

      const variantId = String(variant?.id || '').split('/').pop();
      if (!variantId) {
        this.#renderVariant(this.#productFallback);
        return;
      }

      const details = this.#variantMap[variantId] || (await this.#loadVariant(variantId));
      if (sequence === this.#selectionSequence && this.isConnected) {
        this.#renderVariant(details || this.#productFallback);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Stone details could not be updated.', error);
      if (sequence === this.#selectionSequence && this.isConnected) this.#renderVariant(this.#productFallback);
    }
  };

  #loadVariant = async (variantId) => {
    if (this.#variantCache.has(variantId)) return this.#variantCache.get(variantId);
    if (!this.dataset.sectionId) return null;

    this.#requestController?.abort();
    this.#requestController = new AbortController();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(url, {
      headers: { Accept: 'text/html' },
      signal: this.#requestController.signal,
    });
    if (!response.ok) throw new Error(`Stone detail section request failed with ${response.status}.`);

    const html = await response.text();
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const responseDetails = responseDocument.querySelector(
      `ig-stone-details[data-product-id="${CSS.escape(this.dataset.productId)}"]`
    );
    if (!responseDetails) throw new Error('Stone detail section response was missing its component.');

    const details = this.#readJson('[data-stone-selected-variant]', responseDetails);
    if (details) this.#variantCache.set(variantId, details);
    return details;
  };

  #renderVariant = (details) => {
    let visibleRows = 0;

    for (const row of this.querySelectorAll('[data-stone-row]')) {
      const value = details?.[row.dataset.stoneRow];
      const output = row.querySelector('[data-stone-value]');
      if (!(output instanceof HTMLElement)) continue;

      if (value === null || value === undefined || String(value).trim() === '') {
        row.hidden = true;
        output.textContent = '';
      } else {
        row.hidden = false;
        const formattedValue = String(value).replace(/\s+pc\b/gi, '\u00a0pc');
        if (row.dataset.stoneRow === 'side_stones') {
          output.classList.add('ig-stone-groups');
          output.replaceChildren(
            ...formattedValue
              .split(';')
              .map((part) => part.trim())
              .filter(Boolean)
              .map((part) => {
                const group = document.createElement('span');
                group.textContent = part;
                return group;
              })
          );
        } else {
          output.textContent = formattedValue;
        }
        visibleRows += 1;
      }
    }

    this.hidden = visibleRows === 0;
  };
}

if (!customElements.get('ig-stone-details')) {
  customElements.define('ig-stone-details', IgStoneDetails);
}
